import _ from 'lodash';

import { ausProviderOptions, resetAusProviderOptions } from 'constants/loanActionEditor';
import { buildExpressionTree } from 'utils/expressions';

import {
  getActionApiFormatFromFormFormat,
  getFormActionFormatFromApiFormat,
} from './LoanActionTransformations.utils';

const buildCriteriaExpression = ({
  friendlyExpression,
  schemaParams,
  isNewExpressionsParsingLogicEnabled,
}) => ({
  value: buildExpressionTree(friendlyExpression, {
    schemaParams,
    isNewExpressionsParsingLogicEnabled,
  }),
  userFriendlyValue: friendlyExpression,
});

/**
 * Converts API `loanActionTypes` (manual-action item JSON) into loan-action editor form items.
 * @param {{ loanActionTypes: Array, options: object }} params `options` = useLoanActionItemsOptions()
 * @returns {Array}
 */
export const fromLoanActionTypes = ({ loanActionTypes, options }) =>
  _.sortBy(loanActionTypes, 'executionOrder')
    .map(
      getFormActionFormatFromApiFormat({
        fieldsMap: options.loanActionFieldsMap,
        actionTypeOptions: options.actionTypeOptions,
        isAutomatedActions: false,
        tagsOptions: options.tagsOptions,
        checklistsOptions: options.checklistsOptions,
        customFieldDefinitionOptions: options.customFieldDefinitionOptions,
        formsOptions: options.formsOptions,
        formBundlesOptions: options.formBundlesOptions,
        documentTypesOptions: options.documentTypesOptions,
        reportsOptions: options.extendedReportsOptions,
        fieldsOptions: options.extendedFieldsOptions,
        runAusOptions: options.runAusOptions,
        ausProviderOptions,
        resetAusProviderOptions,
        loanRoleOptions: options.loanRoleOptions,
        securityGroupOptions: options.securityGroupOptions,
        dynamicFieldsOptions: options.dynamicFieldsOptions,
        emailTemplatesOptions: options.emailTemplatesOptions,
        halcyonFormOptions: options.halcyonFormOptions,
        taskTypes: options.taskTypes,
        loanEditorCustomFormsOptions: options.loanEditorCustomFormsOptions,
        usersOptions: options.usersOptions,
        floodProductOptions: options.floodProductOptions,
        impoundTypeOptions: options.allImpoundTypeOptions,
      }),
    )
    .filter(Boolean);

/**
 * Converts loan-action editor form items into the API `loanActionTypes` payload.
 * @param {{ actions: Array, options: object }} params `options` = useLoanActionItemsOptions()
 * @returns {Array}
 */
export const toLoanActionTypesPayload = ({ actions, options }) => {
  const {
    schemaParams,
    loanRoleCriteriaSchemaParams,
    isNewExpressionsParsingLogicEnabled,
    loanActionFieldsMap,
    halcyonExpressionSchemaParams,
    dynamicFieldsOptions,
    arithmeticOperationsSchemaParams,
  } = options;

  return actions.map((formItem, index) => {
    const loanRoleFriendlyExpression = formItem.loanRole?.[0]?.loanRoleCriteriaFriendlyExpression;
    const closingPortalEmailFriendlyExpression =
      formItem.grantClosingPortalAccess?.[0]?.emailFriendlyExpression;

    return getActionApiFormatFromFormFormat({
      formItem,
      index,
      loanActionFieldsMap,
      buildExpressionTree,
      halcyonExpressionSchemaParams,
      isNewExpressionsParsingLogicEnabled,
      dynamicFieldsOptions,
      loanRoleCriteria: loanRoleFriendlyExpression
        ? buildCriteriaExpression({
            friendlyExpression: loanRoleFriendlyExpression,
            schemaParams: loanRoleCriteriaSchemaParams,
            isNewExpressionsParsingLogicEnabled,
          })
        : null,
      closingPortalEmailExpression: closingPortalEmailFriendlyExpression
        ? buildCriteriaExpression({
            friendlyExpression: closingPortalEmailFriendlyExpression,
            schemaParams,
            isNewExpressionsParsingLogicEnabled,
          })
        : null,
      savePendingChangesPermissions: formItem.permissions?.map(
        ({ permissionFriendlyExpression, canCancel, canSave, canViewOthers }) => ({
          expression: buildCriteriaExpression({
            friendlyExpression: permissionFriendlyExpression,
            schemaParams,
            isNewExpressionsParsingLogicEnabled,
          }),
          canCancel: !!canCancel,
          canSave: !!canSave,
          canViewOthers: !!canViewOthers,
        }),
      ),
      arithmeticOperationsSchemaParams,
    });
  });
};
